/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useRouter, useTranslations } from "@i18n/next";
import {
  ERROR_CODE_NAME_REQUIRED_TO_JOIN,
  getAmountsCart,
  getCartIsPaymentRequired,
  getIsCartNameRequiredError,
  getTrialDiscountPercent,
  getTrialEndDate,
  getTrialLength,
  getTrialType,
} from "@packages/payments";
import { PromotionalOffer } from "@packages/payments/blocks";
import {
  isDefaultPlan,
  useRequestCart,
  useRequestProducts,
  useRequestPublicCart,
  useRequestPublicProducts,
  useSession,
} from "@packages/sdk";
import {
  Button,
  Container,
  numericPixels,
  PurplePresentIcon,
  spacing,
  Text,
  TextInput,
  useAlert,
  useGetNumberFormattedFn,
  useSearchParamState,
} from "@packages/ui";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

const baseStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",
  gap: spacing.ml,
  marginTop: spacing.l,
  padding: spacing.m,
  width: spacing.full,
};

const imageStyles: Interpolation = {
  alignSelf: "center",
  maxWidth: numericPixels[160],
};

const titleStyles: Interpolation = {
  alignSelf: "center",
};

const formStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ml,
};

type RedeemCode = {
  code: string;
};

export const RedeemClient = () => {
  const t = useTranslations();
  const router = useRouter();
  const alert = useAlert();
  const { status } = useSession();
  const [promoCode, setPromoCode] = useSearchParamState({ key: "referrer" });
  const { query: products } = useRequestProducts();
  const { query: publicProducts } = useRequestPublicProducts();
  const productList = products.data ?? publicProducts.data ?? [];
  const product = productList.find((product) => isDefaultPlan(product));

  const { query: cart } = useRequestCart({
    priceId: product?.price.id,
    promoCode,
    retry: false,
  });
  const { query: publicCart } = useRequestPublicCart({
    priceId: product?.price.id,
    promoCode,
    retry: false,
  });

  // A missing name isn't an error here; checkout collects it before payment.
  const cartErrors = cart.data?.errors?.filter(
    (err) => err.code !== ERROR_CODE_NAME_REQUIRED_TO_JOIN,
  );

  useEffect(() => {
    if (products.isError) {
      showError(products.error.message);
    } else if (publicProducts.isError) {
      showError(publicProducts.error.message);
    } else if (cart.isError) {
      showError(cart.error.message);
    } else if (publicCart.isError) {
      showError(publicCart.error.message);
    } else if (cart.isSuccess && !!cartErrors?.length) {
      showError(cartErrors[0].error);
      setPromoCode("");
    } else if (status !== "unauthenticated") {
      // The public cart also loads while the session is still resolving, so
      // its errors only apply once we know the user is signed out.
      return;
    } else if (
      publicCart.isSuccess &&
      getIsCartNameRequiredError({ cart: publicCart.data })
    ) {
      // A community-join promo needs an account with a name. Sign in first and
      // come back here with the code; checkout collects a missing name.
      router.replace(
        `/login?redirect=${encodeURIComponent(`/redeem?referrer=${encodeURIComponent(promoCode)}`)}`,
      );
    } else if (publicCart.isSuccess && !!publicCart.data?.errors?.length) {
      showError(publicCart.data.errors[0].error);
      setPromoCode("");
    }
  }, [
    status,
    publicCart.data,
    publicCart.error,
    cart.data,
    cart.error,
    products.error,
    publicProducts.error,
  ]);

  const onSubmit = useCallback(
    async ({ code }: RedeemCode) => {
      if (!!code?.length && (!promoCode?.length || promoCode !== code)) {
        setPromoCode(code);
      } else if (!!promoCode?.length) {
        router.push(
          `/subscribe/checkout?promoCode=${code}&priceId=${product.price.id}`,
        );
      }
    },
    [promoCode, product],
  );

  const showError = (error: string) => {
    alert.show({
      title: t("general_word_error"),
      description: error,
    });
  };

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid, isSubmitting },
  } = useForm<RedeemCode>({ defaultValues: { code: promoCode } });

  const cartData = cart.data ?? publicCart.data;
  const amounts = useMemo(() => getAmountsCart({ cart: cartData }), [cartData]);
  const { getNumberFormattedFn } = useGetNumberFormattedFn();
  const getNumberFormatted = useMemo(
    () => getNumberFormattedFn({ currency: cartData?.currency ?? "usd" }),
    [cartData, getNumberFormattedFn],
  );
  const isPaymentRequired = useMemo(
    () => (cartData ? getCartIsPaymentRequired({ cart: cartData }) : true),
    [cartData],
  );
  const trialDiscountPercent = useMemo(
    () => getTrialDiscountPercent({ cart: cartData }),
    [cartData],
  );
  const trialEndDate = useMemo(
    () => getTrialEndDate({ cart: cartData }),
    [cartData],
  );
  const trialLength = useMemo(
    () => getTrialLength({ cart: cartData }),
    [cartData],
  );
  const trialType = useMemo(() => getTrialType({ cart: cartData }), [cartData]);

  return (
    <Container maxWidth={true} size="xs" css={baseStyles}>
      <PurplePresentIcon css={imageStyles} />
      <Text size="l" type="headline" css={titleStyles}>
        {t("order_summary_gc_redeem")}
      </Text>
      {(cart.data || publicCart.data) && !!promoCode?.length && (
        <PromotionalOffer
          amounts={amounts}
          cart={cartData}
          getNumberFormatted={getNumberFormatted}
          isPaymentRequired={isPaymentRequired}
          trialDiscountPercent={trialDiscountPercent}
          trialEndDate={trialEndDate}
          trialLength={trialLength}
          trialType={trialType}
        />
      )}
      <form css={formStyles} onSubmit={handleSubmit(onSubmit)}>
        <TextInput.Field
          control={control}
          name="code"
          placeholder={t("checkout_summary_enter_code_input")}
        />
        <Button
          type="submit"
          disabled={
            (!isDirty && !promoCode?.length) || !isValid || isSubmitting
          }
          size="l"
          isFullWidth={true}
        >
          {t("onboarding_button_continue")}
        </Button>
      </form>
    </Container>
  );
};
